import React, { useEffect, useId, useRef } from 'react';
import WgSparkle from './WgSparkle';
import './Spinner.css';

/**
 * Spinner — 1:1 port of `apps/WunderGuide/common/Spinner.tsx`. Three stacked
 * effects in front of a gradient border runner:
 *  1. variable-speed rotation (slow → fast → medium) on the sparkle
 *  2. gentle continuous pulse (scale 1 → 1.12)
 *  3. random "twinkle" jolts (extra rotation + scale flash) every 0.8–2s
 */
export interface SpinnerProps {
  size?: 'small' | 'medium' | 'large';
  color?: string;
  className?: string;
}

const SIZE_MAP = { small: 32, medium: 48, large: 64 } as const;

const Spinner: React.FC<SpinnerProps> = ({
  size = 'medium',
  color = '#fff',
  className = '',
}) => {
  const sparkleRef = useRef<HTMLDivElement | null>(null);
  const gradId = useId();
  const px = SIZE_MAP[size];
  const sparklePx = Math.round(px * 0.75);
  const radius = px / 2 - 2;
  const center = px / 2;
  const dash = Math.PI * radius;

  // Twinkle scheduler — mirrors RN Spinner's randomised rotation + scale flash.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      const interval = 800 + Math.random() * 1200;
      const node = sparkleRef.current;
      if (node && typeof node.animate === 'function') {
        const rotation = 12 + Math.random() * 18;
        node.animate(
          [
            { transform: `rotate(${rotation}deg) scale(1.35)`, offset: 0 },
            {
              transform: `rotate(${rotation * 0.5}deg) scale(1.2)`,
              offset: 0.3,
            },
            { transform: 'rotate(0deg) scale(1)', offset: 1 },
          ],
          { duration: 500, easing: 'ease-out', composite: 'add' },
        );
      }
      timer = setTimeout(schedule, interval);
    };
    timer = setTimeout(schedule, 400);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className={`wg-spinner ${className}`.trim()}
      style={{ width: px, height: px }}
    >
      <div className="wg-spinner__ring">
        <svg width={px} height={px} aria-hidden>
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={color} stopOpacity="0.8" />
              <stop offset="30%" stopColor={color} stopOpacity="0.5" />
              <stop offset="60%" stopColor={color} stopOpacity="0.2" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={`url(#${gradId})`}
            strokeWidth={2}
            fill="none"
            strokeDasharray={`${dash} ${dash}`}
          />
        </svg>
      </div>
      <div className="wg-spinner__pulse">
        <div className="wg-spinner__spin">
          <div ref={sparkleRef} className="wg-spinner__sparkle">
            <WgSparkle size={sparklePx} color={color} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Spinner;
